import * as React from "react";
import { createStorefrontApiClient } from "@shopify/storefront-api-client";

// Debug environment variables
// console.log("Environment variables:", {
//   storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL,
//   hasToken: !!process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
// });

// Get store domain from environment or default to your store
const storeDomain =
  process.env.GATSBY_SHOPIFY_STORE_URL || "houseofvoltaire.myshopify.com";

// Get token from environment
const storefrontToken = process.env.GATSBY_STOREFRONT_ACCESS_TOKEN;

if (!storefrontToken) {
  console.error(
    "Storefront API token is missing. Please check your environment variables."
  );
  throw new Error(
    "GATSBY_STOREFRONT_ACCESS_TOKEN environment variable is not set"
  );
}

const client = createStorefrontApiClient({
  storeDomain,
  apiVersion: "2025-10",
  publicAccessToken: storefrontToken,
});

// Helper function to transform GraphQL cart data to expected format
const transformCartData = cartData => {
  // console.log("Raw cart data:", cartData);

  if (!cartData) return { lineItems: [] };

  const transformed = {
    ...cartData,
    id: cartData.id,
    webUrl: cartData.checkoutUrl, // Cart API uses checkoutUrl instead of webUrl
    subtotalPriceV2: cartData.cost?.subtotalAmount || {
      amount: "0.00",
      currencyCode: "GBP",
    },
    lineItems:
      cartData.lines?.edges?.map(edge => ({
        id: edge.node.id,
        quantity: edge.node.quantity,
        title: edge.node.merchandise.product.title,
        variant: {
          id: edge.node.merchandise.id,
          price: edge.node.merchandise.price.amount,
          priceV2: {
            amount: edge.node.merchandise.price.amount,
            currencyCode: edge.node.merchandise.price.currencyCode,
          },
          product: {
            id: edge.node.merchandise.product.id,
            title: edge.node.merchandise.product.title,
            handle: edge.node.merchandise.product.handle,
          },
          image: {
            src:
              edge.node.merchandise.product.images?.edges?.[0]?.node?.url || "",
            altText:
              edge.node.merchandise.product.images?.edges?.[0]?.node?.altText ||
              "",
          },
          selectedOptions: edge.node.merchandise.selectedOptions || [
            { name: "Title", value: "Default Title" },
          ],
        },
      })) || [],
  };

  // console.log("Transformed cart data:", transformed);
  // console.log("Line items count:", transformed.lineItems.length);

  return transformed;
};

const defaultValues = {
  cart: [],
  isOpen: false,
  loading: false,
  onOpen: () => {},
  onClose: () => {},
  addVariantToCart: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
  client,
  checkout: {
    lineItems: [],
  },
};

export const StoreContext = React.createContext(defaultValues);

const isBrowser = typeof window !== `undefined`;
const localStorageKey = `hov_shopify_cart_id`;

export const StoreProvider = ({ children }) => {
  const [checkout, setCheckout] = React.useState(defaultValues.checkout);
  const [loading, setLoading] = React.useState(false);
  const [didJustAddToCart, setDidJustAddToCart] = React.useState(false);

  const setCheckoutItem = cartData => {
    // console.log("setCheckoutItem called with:", cartData);
    const transformedCart = transformCartData(cartData);
    // console.log("Transformed cart:", transformedCart);
    // console.log("Cart ID:", transformedCart.id);

    if (isBrowser) {
      localStorage.setItem(localStorageKey, transformedCart.id);
      // console.log("Saved cart ID to localStorage:", transformedCart.id);
    }
    setCheckout(transformedCart);
    // console.log("Cart state updated");
  };

  React.useEffect(() => {
    const initializeCart = async () => {
      // console.log("Initializing cart...");
      const existingCartID = isBrowser
        ? localStorage.getItem(localStorageKey)
        : null;

      // console.log("Existing cart ID from localStorage:", existingCartID);

      if (existingCartID && existingCartID !== `null`) {
        try {
          // console.log("Fetching existing cart...");
          const response = await client.request(
            `
              query getCart($id: ID!) {
                cart(id: $id) {
                  id
                  checkoutUrl
                  note
                  cost {
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  lines(first: 100) {
                    edges {
                      node {
                        id
                        quantity
                        merchandise {
                          ... on ProductVariant {
                            id
                            price {
                              amount
                              currencyCode
                            }
                            selectedOptions {
                              name
                              value
                            }
                            product {
                              id
                              title
                              handle
                              images(first: 1) {
                                edges {
                                  node {
                                    url
                                    altText
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            `,
            {
              variables: {
                id: existingCartID,
              },
            }
          );

          // console.log("Existing cart response:", response);

          if (response.data?.cart) {
            // console.log("Setting existing cart");
            setCheckoutItem(response.data.cart);
            return;
          } else {
            // Cart not found (may be expired after 10 days)
            console.log("Cart not found or expired, creating new cart");
            localStorage.setItem(localStorageKey, null);
          }
        } catch (e) {
          console.error("Error fetching existing cart:", e);

          // Check if cart is expired or not found
          const errorMessage = e.message || e.toString();
          if (
            errorMessage.includes("not found") ||
            errorMessage.includes("expired") ||
            errorMessage.includes("Invalid") ||
            e.extensions?.code === "INVALID_CART_ID"
          ) {
            console.log("Cart expired or invalid, will create new cart");
          }

          localStorage.setItem(localStorageKey, null);
        }
      }

      // Create new cart
      // console.log("Creating new cart...");
      try {
        const response = await client.request(
          `
            mutation cartCreate {
              cartCreate(input: {}) {
                cart {
                  id
                  checkoutUrl
                  note
                  cost {
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  lines(first: 100) {
                    edges {
                      node {
                        id
                        quantity
                        merchandise {
                          ... on ProductVariant {
                            id
                            price {
                              amount
                              currencyCode
                            }
                            selectedOptions {
                              name
                              value
                            }
                            product {
                              id
                              title
                              handle
                              images(first: 1) {
                                edges {
                                  node {
                                    url
                                    altText
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                userErrors {
                  code
                  field
                  message
                }
              }
            }
          `
        );

        // console.log("New cart response:", response);

        if (response.data?.cartCreate?.cart) {
          // console.log(
          //   "Setting new cart with ID:",
          //   response.data.cartCreate.cart.id
          // );
          setCheckoutItem(response.data.cartCreate.cart);
        } else {
          console.error(
            "Failed to create cart:",
            response.data?.cartCreate?.userErrors
          );
        }
      } catch (error) {
        console.error("Error creating new cart:", error);
      }
    };

    initializeCart();
  }, []);

  const addVariantToCart = async (variantId, quantity) => {
    // console.log("Adding to cart:", {
    //   variantId,
    //   quantity,
    //   cartId: checkout.id,
    // });

    // If no cart exists, create one first
    if (!checkout.id) {
      // console.log("No cart exists, creating new cart first...");
      try {
        const createResponse = await client.request(
          `
            mutation cartCreate {
              cartCreate(input: {}) {
                cart {
                  id
                  checkoutUrl
                  note
                  cost {
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  lines(first: 100) {
                    edges {
                      node {
                        id
                        quantity
                        merchandise {
                          ... on ProductVariant {
                            id
                            price {
                              amount
                              currencyCode
                            }
                            selectedOptions {
                              name
                              value
                            }
                            product {
                              id
                              title
                              handle
                              images(first: 1) {
                                edges {
                                  node {
                                    url
                                    altText
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                userErrors {
                  code
                  field
                  message
                }
              }
            }
          `
        );

        if (createResponse.data?.cartCreate?.cart) {
          setCheckoutItem(createResponse.data.cartCreate.cart);
          // Update checkout state immediately for this operation
          const newCart = transformCartData(
            createResponse.data.cartCreate.cart
          );

          // Now add the item to the newly created cart
          return addItemToExistingCart(newCart.id, variantId, quantity);
        } else {
          console.error(
            "Failed to create cart:",
            createResponse.data?.cartCreate?.userErrors
          );
          return;
        }
      } catch (error) {
        console.error("Error creating cart:", error);
        return;
      }
    } else {
      // Cart exists, add item to it
      return addItemToExistingCart(checkout.id, variantId, quantity);
    }
  };

  const addItemToExistingCart = async (cartId, variantId, quantity) => {
    setLoading(true);

    try {
      const response = await client.request(
        `
          mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
              cart {
                  id
                  checkoutUrl
                  note
                  cost {
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          price {
                            amount
                            currencyCode
                          }
                          selectedOptions {
                            name
                            value
                          }
                          product {
                            id
                            title
                            handle
                            images(first: 1) {
                              edges {
                                node {
                                  url
                                  altText
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              userErrors {
                code
                field
                message
              }
            }
          }
        `,
        {
          variables: {
            cartId: cartId,
            lines: [
              {
                merchandiseId: variantId,
                quantity: parseInt(quantity, 10),
              },
            ],
          },
        }
      );

      // console.log("Add to cart response:", response);

      if (response.data?.cartLinesAdd?.cart) {
        setCheckoutItem(response.data.cartLinesAdd.cart);
        setDidJustAddToCart(true);
        setTimeout(() => setDidJustAddToCart(false), 3000);
      }

      if (response.data?.cartLinesAdd?.userErrors?.length > 0) {
        console.error("Cart errors:", response.data.cartLinesAdd.userErrors);

        // Check if cart is invalid/expired
        const invalidCartError = response.data.cartLinesAdd.userErrors.find(
          error =>
            error.code === "INVALID_CART_ID" || error.message?.includes("cart")
        );

        if (invalidCartError && isBrowser) {
          console.log("Cart expired, clearing localStorage");
          localStorage.setItem(localStorageKey, null);
        }
      }
    } catch (error) {
      console.error("Error adding item to cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeLineItem = async (cartID, lineItemID) => {
    setLoading(true);

    try {
      const response = await client.request(
        `
          mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
            cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
              cart {
                  id
                  checkoutUrl
                  note
                  cost {
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          price {
                            amount
                            currencyCode
                          }
                          selectedOptions {
                            name
                            value
                          }
                          product {
                            id
                            title
                            handle
                            images(first: 1) {
                              edges {
                                node {
                                  url
                                  altText
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              userErrors {
                code
                field
                message
              }
            }
          }
        `,
        {
          variables: {
            cartId: cartID,
            lineIds: [lineItemID],
          },
        }
      );

      if (response.data?.cartLinesRemove?.cart) {
        setCheckoutItem(response.data.cartLinesRemove.cart);
      }
    } catch (error) {
      console.error("Error removing item from cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateLineItem = async (cartID, lineItemID, quantity) => {
    setLoading(true);

    try {
      const response = await client.request(
        `
          mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart {
                  id
                  checkoutUrl
                  note
                  cost {
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          price {
                            amount
                            currencyCode
                          }
                          selectedOptions {
                            name
                            value
                          }
                          product {
                            id
                            title
                            handle
                            images(first: 1) {
                              edges {
                                node {
                                  url
                                  altText
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              userErrors {
                code
                field
                message
              }
            }
          }
        `,
        {
          variables: {
            cartId: cartID,
            lines: [
              {
                id: lineItemID,
                quantity: parseInt(quantity, 10),
              },
            ],
          },
        }
      );

      if (response.data?.cartLinesUpdate?.cart) {
        setCheckoutItem(response.data.cartLinesUpdate.cart);
      }
    } catch (error) {
      console.error("Error updating cart item:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
        removeLineItem,
        updateLineItem,
        checkout,
        loading,
        didJustAddToCart,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};
